import CloseIcon from "@mui/icons-material/Close";
import {
	DialogActionsProps,
	DialogContentProps,
	DialogActions as MuiDialogActions,
	DialogContent as MuiDialogContent,
	DialogTitle as MuiDialogTitle,
	styled,
} from "@mui/material";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import React, { ComponentType, ReactNode } from "react";

interface DialogTitleProps {
	id: string;
	children: React.ReactNode;
	onClose: () => void;
}

const StyledDialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	gap: theme.spacing(0),
	margin: 0,
	padding: theme.spacing(2),
	paddingRight: theme.spacing(7),
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
	position: "absolute",
	right: theme.spacing(1),
	top: theme.spacing(1),
	color: theme.palette.grey[500],
}));

const DialogTitle = (props: DialogTitleProps) => {
	const { children, onClose, ...other } = props;
	return (
		<StyledDialogTitle {...other}>
			{children}
			{onClose && (
				<StyledCloseButton aria-label="close" onClick={onClose}>
					<CloseIcon />
				</StyledCloseButton>
			)}
		</StyledDialogTitle>
	);
};

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
	padding: theme.spacing(2),
	minWidth: 600,
	overflow: "hidden",
}));

const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
	margin: 0,
	padding: theme.spacing(1),
}));

interface MESFModalProps extends Omit<DialogProps, "onClose" | "open"> {
	handleClose: (shouldUpdate?: boolean) => void;
	open: boolean;
	id?: string;
	maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
	children: ReactNode;
	title?: string;
	subtitle?: string;
}

function Modal({
	children,
	title,
	subtitle,
	handleClose,
	open,
	id = "mesf-modal",
	maxWidth = "lg",
}: MESFModalProps) {
	return (
		<Dialog
			onClose={() => handleClose()}
			aria-labelledby={id}
			open={open}
			maxWidth={maxWidth}
			style={{ minWidth: 320 }}
		>
			<DialogTitle id={id} onClose={() => handleClose()}>
				{title}
				{subtitle ? (
					<Typography variant="caption" color="text.secondary" display="block">
						{subtitle}
					</Typography>
				) : null}
			</DialogTitle>
			{children}
		</Dialog>
	);
}

export const MesfModal: typeof Modal & {
	Content: ComponentType<DialogContentProps>;
	Actions: ComponentType<DialogActionsProps>;
} = Object.assign(Modal, {
	Content: DialogContent as ComponentType<DialogContentProps>,
	Actions: DialogActions as ComponentType<DialogActionsProps>,
});

export default MesfModal;
